import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";

/* main's local CarouselArrow lived here and is gone: it was geometrically
   identical to @/components/ui/drawn-arrow (46x16 on a 0 0 74 26 viewBox, the
   same four paths, currentColor, strokeWidth 2.25). Two branches solved the
   same problem the same way at the same time; keeping both definitions is how
   they drift, so the shared module is the survivor. */

import { requestCheckout } from "@/lib/open-checkout";
import { DrawnArrow } from "@/components/ui/drawn-arrow";

/**
 * OnPrompt Studio sections — ported from Isaac's Claude Design project
 * "OnPrompt Studio Section.dc.html" (design project 9be67a9d…). Two bespoke
 * value-stack bands, one per workshop page:
 *
 *   OnPromptStudioSection  — design workshop page, DARK variant. The "$799
 *                            design library, included" pitch with an
 *                            interactive 5-tab walkthrough player.
 *   MeetRogerSection       — cowork page, LIGHT variant. The "meet Roger,
 *                            your AI researcher, free" pitch.
 *
 * DESIGN-SYSTEM DEVIATIONS (faithful to the source design, flagged for review):
 *   - border-radius 12px (frames/cards), 8px (thumbs), 999px (pills/badges)
 *     exceed the repo's 4px cap. Kept to match Isaac's design.
 *   - The source's decorative radial-gradient glow behind each video was
 *     DROPPED to respect the no-background-gradients rule (it was a subtle
 *     edge halo behind an opaque video — no visible loss).
 *
 * Both bands are full-bleed; content is constrained to container-content
 * (1280px) to match the source's 1280px canvas. Colors flip on `theme`.
 *
 * PERF: media is web-optimized (720p mains ~0.4–3MB, 480w thumb loops
 * ~60–330KB, all H.264 +faststart, audio stripped) and lazy: nothing
 * downloads until the section is within ~600px of the viewport. Until then
 * (and during SSR) every frame paints its poster .jpg, so there's no layout
 * shift and the players "fill in" as you scroll to them. See /images/onprompt.
 */

type Theme = "dark" | "light";

/** Flip to true once the section is within `rootMargin` of the viewport, so
 *  videos mount (and start downloading) only when the user is approaching.
 *  SSR / no-IO environments render eagerly (returns true). */
function useInView<T extends HTMLElement>(rootMargin = "600px") {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return { ref, inView };
}

const THEME = {
  dark: {
    bg: "#151514",
    text: "#EFEDE7",
    muted: "rgba(239,237,231,.68)",
    logo: "/images/onprompt/onprompt-logo-light-800.webp",
    frameBorder: "rgba(239,237,231,.12)",
    pillBorder: "rgba(239,237,231,.22)",
    pillText: "rgba(239,237,231,.7)",
    divider: "rgba(239,237,231,.12)",
    thumbIdleBorder: "rgba(239,237,231,.16)",
    thumbIdleLabel: "rgba(239,237,231,.5)",
    link: "var(--cream)",
  },
  light: {
    bg: "var(--cream)",
    text: "#151514",
    muted: "rgba(21,21,20,.68)",
    logo: "/images/onprompt/onprompt-logo-dark-800.webp",
    frameBorder: "rgba(21,21,20,.12)",
    pillBorder: "rgba(21,21,20,.2)",
    pillText: "rgba(21,21,20,.7)",
    divider: "rgba(21,21,20,.14)",
    thumbIdleBorder: "rgba(21,21,20,.16)",
    thumbIdleLabel: "rgba(21,21,20,.5)",
    link: "var(--charcoal)",
  },
} satisfies Record<Theme, Record<string, string>>;

const MONO = "'JetBrains Mono', monospace";
const SERIF = "'Copernicus', Georgia, serif";
// Original Gochi — shared with Cowork's MeetRoger StudioHeader note, so keep it
// as-is. The design section applies var(--font-hand) (Crayonize) inline instead.
const HAND = "'Gochi Hand', cursive";
const FULL_BLEED = { marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" } as const;

/** Shared header: optional prefix + OnPrompt logo + optional STUDIO wordmark + handwritten note. */
function StudioHeader({
  theme,
  note,
  prefix,
  showWordmark = true,
}: {
  theme: Theme;
  note: string;
  prefix?: string;
  showWordmark?: boolean;
}) {
  const t = THEME[theme];
  return (
    <div className="flex items-center justify-between gap-4 mb-10 md:mb-11">
      <div className="flex items-center gap-3 flex-wrap">
        {prefix && (
          <span
            style={{
              fontFamily: MONO,
              fontSize: 11,
              letterSpacing: ".18em",
              textTransform: "uppercase",
              color: "var(--terra)",
            }}
          >
            {prefix}
          </span>
        )}
        <img
          src={t.logo}
          alt="OnPrompt"
          loading="lazy"
          decoding="async"
          /* Both logo files are 800x128; the attributes give the browser the
             aspect ratio so the 19/22px-tall box is reserved before the bytes
             arrive (Lighthouse "unsized-images", 2026-09-09). */
          width={800}
          height={128}
          className="h-[19px] md:h-[22px] w-auto block"
        />
        {showWordmark && (
          <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".24em", color: "var(--terra)" }}>
            STUDIO
          </span>
        )}
      </div>
      {note && (
        <span
          className="hidden sm:inline-block"
          style={{ fontFamily: HAND, fontSize: 19, color: "var(--terra)", transform: "rotate(-3deg)" }}
        >
          {note}
        </span>
      )}
    </div>
  );
}

/** Primary + secondary CTA pair. Primary is the standard style-guide CTA
 *  button and opens the checkout sheet exactly like every other reserve
 *  button on the page (href is the no-listener/SSR fallback). Secondary is
 *  a plain underlined outbound link to OnPrompt. */
function CtaRow({
  theme,
  ticketHref,
  secondaryLabel,
  secondaryHref = "https://onprompt.studio/",
  align = "row",
}: {
  theme: Theme;
  ticketHref: string;
  secondaryLabel: string;
  secondaryHref?: string;
  align?: "row" | "col";
}) {
  const t = THEME[theme];
  return (
    <div
      className={
        align === "col"
          ? "flex flex-col items-center gap-4"
          : "flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6"
      }
    >
      <a
        href={ticketHref}
        className="btn btn-cta btn-lg w-full sm:w-auto"
        onClick={(e) => {
          if (requestCheckout()) e.preventDefault();
        }}
      >
        Reserve your spot
      </a>
      <a
        href={secondaryHref}
        target="_blank"
        rel="noopener noreferrer"
        className="ds-secondary type-label underline text-center sm:text-left"
        style={{ color: t.link, textUnderlineOffset: 4 }}
      >
        {secondaryLabel}
      </a>
    </div>
  );
}

/* ── Design library categories ─────────────────────────────────────────────
   Each category renders as a simple row: name + a sentence or two on the left,
   one still image on the right. */
const BASE = "/images/onprompt/";

export interface OnPromptFeature {
  name: string;
  img: string;
  desc: string;
}

/** The full library. A page may pass its own subset via `features` — see the
 *  Sales page, which shows three because the other two are web-design features
 *  a sales team has no use for. */
export const ONPROMPT_FEATURES: OnPromptFeature[] = [
  {
    name: "Website templates",
    img: `${BASE}step-by-step-web.jpg`,
    desc: "Full, multi-page site designs built and ready to ship. Start from a finished layout instead of a blank page.",
  },
  {
    name: "Backgrounds",
    img: `${BASE}background-walkthrough.jpg`,
    desc: "A library of textures and backdrops to drop behind any section, all on-brand and aligned to the grid.",
  },
  {
    name: "Section components",
    img: `${BASE}section-components.webp`,
    desc: "Prebuilt heroes, feature grids, pricing tables, and CTAs. Assemble a page from parts that already work together.",
  },
  {
    // Reuses the OnPrompt screenshots Isaac features on the cowork page.
    name: "Motion library",
    img: `${BASE}onprompt-motion.webp`,
    desc: "More than 200 named animation effects. Add movement to any element just by naming the one you want.",
  },
  {
    name: "AI agents",
    img: `${BASE}onprompt-agents.webp`,
    desc: "Working teammates like Roger, our business-development researcher. Configured and ready to put to work.",
  },
];

/* ── Section 1: OnPrompt Studio design library (design workshop page) ─────── */
export function OnPromptStudioSection({
  theme = "dark",
  ticketHref = "#reserve",
  paddingClass = "section-padding-y",
  fullBleed = true,
  features = ONPROMPT_FEATURES,
  longArrows = false,
  maxCards,
}: {
  theme?: Theme;
  ticketHref?: string;
  paddingClass?: string;
  fullBleed?: boolean;
  /** Defaults to the whole library. Pass a subset to show fewer rows. */
  features?: OnPromptFeature[];
  /* Opt in to the long stroked arrow (and the wider hit area it needs) so the
     carousels on a page share one control. Off by default: every other page
     keeps the lucide chevrons it already ships. The Claude Code page turns it
     on; so does Sales (Esther, 2026-09-01). */
  longArrows?: boolean;
  /* Show only the first N features. The cards, the dots, the card refs and the
     arrows' clamp all read from the same trimmed list, so the JS and the markup
     stay in agreement. Trimming the deck in CSS instead left the arrows paging
     toward cards with no layout box: Next stayed lit at the end of a short deck
     and did nothing when pressed.

     `features` and `maxCards` compose: features chooses WHICH rows, maxCards
     caps HOW MANY. Sales passes features (three of five); Code passes maxCards. */
  maxCards?: number;
}) {
  const t = THEME[theme];
  /* The two props compose here rather than fighting: `features` (the prop, above)
     chooses WHICH rows — Sales passes three of five — and `maxCards` caps HOW
     MANY, which is how the Code page trims. main's line read
     `const features = maxCards ? FEATURES.slice(...) : FEATURES`, which both
     shadowed the prop and named a const this branch had renamed to
     ONPROMPT_FEATURES when it was exported. `cards` is the rendered deck; every
     read below goes through it, so the JS and the markup stay in agreement. */
  const cards = maxCards ? features.slice(0, maxCards) : features;
  const { ref, inView } = useInView<HTMLElement>();
  // Mobile carousel: track which card is snapped into view for the dots.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardEls = useRef<Array<HTMLElement | null>>([]);
  const [card, setCard] = useState(0);
  useEffect(() => {
    const root = scrollerRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setCard(Number((e.target as HTMLElement).dataset.idx));
        });
      },
      { root, threshold: 0.6 },
    );
    cardEls.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);
  const goTo = (i: number) => {
    const n = Math.max(0, Math.min(cards.length - 1, i));
    cardEls.current[n]?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };

  return (
    <section
      ref={ref}
      aria-label="OnPrompt Studio design library — included with your workshop seat"
      style={{ ...(fullBleed ? FULL_BLEED : {}), background: t.bg, color: t.text }}
      data-testid="onprompt-studio-section"
    >
      <div
        className={`container-content ${paddingClass}`}
        style={{ boxSizing: "border-box", maxWidth: "calc(1280px + 8rem)" }}
      >
        {/* items-start so the left copy can pin while the taller right column
            of image cards scrolls past it. */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,.9fr)_minmax(0,1.1fr)] gap-10 lg:gap-16 items-start">
          {/* LEFT — logo lockup + headline + text + CTA, all pinned together
              below the fixed navbar (72px) + event bar (~41px) so the logo
              stays visible; static on mobile. */}
          <div className="lg:sticky" style={{ top: "clamp(156px, 19vh, 188px)", alignSelf: "start" }}>
            <StudioHeader theme={theme} prefix="In collaboration with" note="" showWordmark={false} />
            <h2
              style={{
                fontFamily: SERIF,
                fontWeight: 600,
                fontSize: "clamp(30px, 3.6vw, 42px)",
                lineHeight: 1.08,
                letterSpacing: "-.025em",
                margin: "0 0 36px",
              }}
            >
              Free access to <strong className="op-brand">OnPrompt Studio</strong> with your registration{" "}
              <span
                className="whitespace-nowrap"
                style={{
                  fontFamily: "var(--font-hand)",
                  fontWeight: 700,
                  color: "var(--terra)",
                  display: "inline-block",
                  transform: "rotate(-2deg)",
                }}
              >
                ($799 value)
              </span>
            </h2>
            <p
              style={{
                fontSize: 16.5,
                lineHeight: 1.65,
                color: t.muted,
                margin: "0 0 48px",
                maxWidth: "32em",
              }}
            >
              OnPrompt Studio is a curated, members-only library of production-ready web designs, motion
              graphics, textures, and AI agents. Every workshop seat gets it, on us.
            </p>
            {/* Desktop CTA lives with the pinned copy; on mobile it moves to
                the bottom, after the carousel. */}
            <div className="hidden lg:block">
              <CtaRow theme={theme} ticketHref={ticketHref} secondaryLabel="Preview the library →" />
            </div>
          </div>

          {/* RIGHT — handwritten note (right-aligned), then the library:
              a horizontal swipe carousel on mobile, a vertical scroll on desktop.
              Name + description under each. */}
          <div>
            <div className="hidden sm:flex justify-end" style={{ marginBottom: "clamp(20px, 3vw, 32px)" }}>
              <span
                style={{
                  fontFamily: "var(--font-hand)",
                  fontWeight: 700,
                  fontSize: "clamp(24px, 2.2vw, 30px)",
                  lineHeight: 1.1,
                  color: "var(--terra)",
                  transform: "rotate(-3deg)",
                }}
              >
                <span className="op-note-text">Lifetime access, free with your workshop</span>
                <span className="op-note-spark" aria-hidden="true" />
              </span>
            </div>
            <div
              ref={scrollerRef}
              className="flex gap-5 overflow-x-auto snap-x snap-mandatory lg:flex-col lg:gap-[clamp(48px,6vw,80px)] lg:overflow-visible lg:snap-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {cards.map((f, i) => (
              <figure
                key={f.name}
                ref={(el) => {
                  cardEls.current[i] = el;
                }}
                data-idx={i}
                className="m-0 shrink-0 w-[84%] sm:w-[68%] snap-start lg:w-full lg:shrink"
              >
                <div
                  className="relative overflow-hidden"
                  style={{
                    borderRadius: 12,
                    background: "#0c0c0b",
                    border: `1px solid ${t.frameBorder}`,
                    aspectRatio: "16 / 9",
                  }}
                >
                  <img
                    src={inView ? f.img : undefined}
                    alt={`OnPrompt Studio — ${f.name}`}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover block"
                  />
                </div>
                <figcaption style={{ marginTop: 18 }}>
                  <h3
                    style={{
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: "clamp(21px, 2vw, 26px)",
                      lineHeight: 1.15,
                      letterSpacing: "-.02em",
                      margin: "0 0 10px",
                    }}
                  >
                    {f.name}
                  </h3>
                  <p style={{ fontSize: 16, lineHeight: 1.6, color: t.muted, margin: 0, maxWidth: "34em" }}>
                    {f.desc}
                  </p>
                </figcaption>
              </figure>
              ))}
            </div>

            {/* Carousel controls — mobile only: prev/next arrows + tappable dots. */}
            <div className="flex lg:hidden items-center justify-center gap-3 mt-7">
              <button
                type="button"
                aria-label="Previous"
                disabled={card === 0}
                onClick={() => goTo(card - 1)}
                className="inline-flex items-center justify-center border-0 cursor-pointer disabled:opacity-30 disabled:cursor-default"
                style={{ width: longArrows ? 64 : 44, height: 44, borderRadius: 999, background: t.thumbIdleBorder, color: t.text }}
              >
                {longArrows ? <DrawnArrow dir="prev" /> : <ChevronLeft className="w-5 h-5" strokeWidth={2} aria-hidden="true" />}
              </button>

              <div className="flex items-center" aria-hidden="true">
                {cards.map((f, i) => (
                  <button
                    key={f.name}
                    type="button"
                    aria-label={f.name}
                    /* The row is aria-hidden: these dots mirror the arrows, so they
                       stay out of the tab order rather than handing a keyboard a
                       control that screen readers are told does not exist. */
                    tabIndex={-1}
                    onClick={() => goTo(i)}
                    className="inline-flex items-center justify-center border-0 p-0 cursor-pointer"
                    style={{ width: 30, height: 30 }}
                  >
                    <span
                      className="block transition-all"
                      style={{
                        width: card === i ? 24 : 9,
                        height: 9,
                        borderRadius: 999,
                        background: card === i ? "var(--terra)" : t.thumbIdleBorder,
                      }}
                    />
                  </button>
                ))}
              </div>

              <button
                type="button"
                aria-label="Next"
                disabled={card === cards.length - 1}
                onClick={() => goTo(card + 1)}
                className="inline-flex items-center justify-center border-0 cursor-pointer disabled:opacity-30 disabled:cursor-default"
                style={{ width: longArrows ? 64 : 44, height: 44, borderRadius: 999, background: t.thumbIdleBorder, color: t.text }}
              >
                {longArrows ? <DrawnArrow dir="next" /> : <ChevronRight className="w-5 h-5" strokeWidth={2} aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile CTA — after the carousel, so the pitch reads
            copy → swipe the library → reserve. */}
        <div className="lg:hidden" style={{ marginTop: "clamp(40px, 9vw, 56px)" }}>
          <CtaRow theme={theme} ticketHref={ticketHref} secondaryLabel="Preview the library →" />
        </div>
      </div>
    </section>
  );
}

/* ── Section 2: Meet Roger (cowork page) ──────────────────────────────────── */
const ROGER_PILLS = ["Prospect research", "Account briefs", "Outreach angles"];

export function MeetRogerSection({
  theme = "light",
  ticketHref = "#tickets",
  paddingClass = "py-10 lg:py-28",
}: {
  theme?: Theme;
  ticketHref?: string;
  paddingClass?: string;
}) {
  const t = THEME[theme];
  const { ref, inView } = useInView<HTMLElement>();
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !inView) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [inView]);
  return (
    <section
      ref={ref}
      aria-label="Meet Roger — your AI researcher, included with your seat"
      style={{ ...FULL_BLEED, background: t.bg, color: t.text }}
      data-testid="meet-roger-section"
    >
      <div
        className={`container-content ${paddingClass}`}
        /* Pin box-sizing stably: .container-content's box-content utility
           resolves inconsistently off- vs on-screen here, flipping the
           computed width and (via the 16:9 frame) the section height as it
           scrolls in — a visible layout shift. Force border-box + a max-width
           bumped by the xl gutter so the content area stays 1280px, stable. */
        style={{ boxSizing: "border-box", maxWidth: "calc(1280px + 8rem)" }}
      >
        <StudioHeader theme={theme} note="free with your workshop ✦" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,.92fr)_minmax(0,1.08fr)] gap-10 lg:gap-14 items-center">
          <div>
            <p
              style={{
                fontFamily: MONO,
                fontSize: 12,
                letterSpacing: ".18em",
                textTransform: "uppercase",
                color: "var(--terra)",
                margin: "0 0 26px",
              }}
            >
              Premium access included with your ticket
            </p>
            <h2
              style={{
                fontFamily: SERIF,
                fontWeight: 600,
                fontSize: "clamp(31px, 4vw, 44px)",
                lineHeight: 1.08,
                letterSpacing: "-.025em",
                margin: "0 0 28px",
              }}
            >
              Join the workshop and get{" "}
              <span style={{ fontStyle: "italic", color: "var(--terra)" }}>
                a custom business-development AI agent.
              </span>
            </h2>
            <p
              style={{
                fontSize: 16.5,
                lineHeight: 1.7,
                color: t.muted,
                margin: "0 0 40px",
                maxWidth: "31em",
              }}
            >
              Roger is a business development researcher that finds prospects, builds account briefs, and
              surfaces outreach angles. Configured and ready, included with every seat.
            </p>
            <CtaRow
              theme={theme}
              ticketHref={ticketHref}
              secondaryLabel="Meet Roger →"
              secondaryHref="https://onprompt.studio/"
            />
          </div>

          <div className="flex flex-col gap-[18px]">
            <div
              className="relative overflow-hidden"
              style={{ borderRadius: 12, background: "#0c0c0b", border: `1px solid ${t.frameBorder}`, aspectRatio: "16 / 9" }}
            >
              <video
                ref={videoRef}
                src={inView ? "/images/onprompt/roger-walkthrough.mp4" : undefined}
                poster={inView ? "/images/onprompt/roger-walkthrough.jpg" : undefined}
                autoPlay
                loop
                muted
                playsInline
                preload="none"
                className="absolute inset-0 w-full h-full block"
                style={{ objectFit: "cover", background: "#0c0c0b" }}
              />
              <div
                className="absolute left-3.5 bottom-3.5"
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  background: "rgba(21,21,20,.62)",
                  backdropFilter: "blur(6px)",
                  fontFamily: MONO,
                  fontSize: 11,
                  letterSpacing: ".03em",
                  color: "#EFEDE7",
                }}
              >
                Roger · biz dev researcher
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {ROGER_PILLS.map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2"
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    border: `1px solid ${t.pillBorder}`,
                    fontFamily: MONO,
                    fontSize: 11.5,
                    color: t.pillText,
                  }}
                >
                  <span
                    className="inline-block"
                    style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--terra)" }}
                  />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Section 3: OnPrompt Studio value stack (cowork page) ─────────────────────
   "In collaboration with OnPrompt" band: a left copy column (handwritten collab
   mark + OnPrompt logo, value headline, blurb) beside a staggered 2×2 image
   grid that bleeds off the right edge (Sherpa-screenshot treatment). Built to
   the repo design system — tokens + type-* classes throughout. Three sanctioned
   deviations, all art-directed by Isaac: image corner radius (var(--radius-md)),
   a charcoal bottom scrim on each tile so the beige caption stays legible, and
   the intentional right-edge bleed. */
const STUDIO_ITEMS = [
  {
    img: "/images/onprompt/onprompt-designs.webp",
    title: "Web Designs",
    alt: "OnPrompt Studio web design template gallery",
    blurb: "Ready-to-ship page templates.",
  },
  {
    img: "/images/onprompt/onprompt-motion.webp",
    title: "Motion Graphics",
    alt: "OnPrompt Studio motion effects and animation library",
    blurb: "200+ named effects, on demand.",
  },
  {
    img: "/images/onprompt/onprompt-agents.webp",
    title: "AI Agents",
    alt: "OnPrompt Studio AI agent library",
    blurb: "Working teammates, like Roger.",
  },
];

/* ── Section 3: OnPrompt Studio value stack (cowork page) ─────────────────────
   Simple centered band: mono eyebrow, serif headline (with a handwritten $799
   accent), a compact subhead, then the four screenshots in ONE row spanning the
   breakout container. Sharp corners (no radius) on the images per Isaac. Built
   to the repo design system — tokens + type-* classes throughout. */
function OnPromptImage({ item }: { item: (typeof STUDIO_ITEMS)[number] }) {
  return (
    <figure className="text-center">
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "16 / 10", border: "var(--border-structural-dark)", background: "var(--card-bg-dark)" }}
      >
        <img
          src={item.img}
          alt={item.alt}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover block"
        />
      </div>
      <figcaption style={{ marginTop: "var(--space-gap-sm)" }}>
        <h3 className="type-h4-sm">{item.title}</h3>
        <p className="type-body-sm" style={{ color: "var(--text-muted-on-dark)", marginTop: "var(--space-gap-xs)" }}>
          {item.blurb}
        </p>
      </figcaption>
    </figure>
  );
}

export function OnPromptStudioValueStack({
  className = "",
  ticketHref = "#tickets",
  exploreHref = "https://onprompt.studio/",
}: {
  className?: string;
  ticketHref?: string;
  exploreHref?: string;
}) {
  return (
    <section
      aria-label="OnPrompt Studio — included free with your workshop seat"
      className={`section-padding-y bg-charcoal text-cream ${className}`}
      /* Full-bleed dark band: break out of the parent container-content so the
         image row can run wider than breakout. Header stays centered inside. */
      style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
      data-testid="onprompt-studio-value-stack"
    >
      {/* Centered header. */}
      <div
        className="text-center mx-auto"
        style={{ maxWidth: "var(--max-w-heading)", paddingLeft: "var(--pad-container)", paddingRight: "var(--pad-container)" }}
      >
        {/* Eyebrow: mono label + white OnPrompt logo. */}
        <div
          className="flex flex-wrap items-center justify-center"
          style={{ gap: "var(--space-gap-xs)", marginBottom: "var(--space-gap-md)" }}
        >
          <span className="type-eyebrow" style={{ color: "var(--terra-lt)", marginBottom: 0 }}>
            In collaboration with
          </span>
          <img
            src="/images/onprompt/onprompt-logo-light-800.webp"
            alt="OnPrompt Studio"
            loading="lazy"
            decoding="async"
            className="h-5 w-auto block"
          />
        </div>
        <h2 className="type-h2">
          Free access to OnPrompt Studio with your registration{" "}
          <span
            className="font-hand"
            style={{ color: "var(--terra-lt)", fontWeight: 700, display: "inline-block", transform: "rotate(-2deg)" }}
          >
            ($799 value)
          </span>
        </h2>
        <p
          className="type-body mx-auto"
          style={{ color: "var(--text-muted-on-dark)", marginTop: "var(--space-gap-md)", maxWidth: "var(--max-w-text)" }}
        >
          OnPrompt Studio is a curated, members-only library of production-ready web designs, motion
          graphics, textures, and AI agents. Every workshop seat gets it, on us.
        </p>
      </div>

      {/* Image row — three large screenshots, wider than breakout (1840px).
          Single column on phones; three across from md. Sharp corners +
          hairline border. */}
      <div
        className="mx-auto"
        style={{
          maxWidth: "1840px",
          paddingLeft: "clamp(20px, 3.5vw, 56px)",
          paddingRight: "clamp(20px, 3.5vw, 56px)",
          marginTop: "var(--space-gap-xl)",
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: "var(--space-gap-md)" }}>
          {STUDIO_ITEMS.map((it) => (
            <OnPromptImage key={it.title} item={it} />
          ))}
        </div>

        {/* CTAs, centered below the row: register (primary) + outbound OnPrompt
            link. Stacks on phones, side by side from sm. */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center"
          style={{ gap: "var(--space-gap-md)", marginTop: "var(--space-gap-lg)" }}
        >
          {/* Same contract as every other reserve CTA on the page: open the
              checkout sheet; href is the no-listener/SSR fallback. */}
          <a
            href={ticketHref}
            className="btn btn-cta btn-lg w-full sm:w-auto"
            onClick={(e) => {
              if (requestCheckout()) e.preventDefault();
            }}
          >
            Reserve your seat
          </a>
          <a
            href={exploreHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 type-label underline"
            style={{ color: "var(--cream)", textUnderlineOffset: 4 }}
          >
            Explore OnPrompt
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}
